import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  createAgentGraph,
  httpTransport,
  INTERACTIVE_UI_TOOLS,
  runTurn,
  type AgentGraph,
  type AgentMessage,
  type AgentTransport,
  type ToolResult,
  type ToolUseBlock,
} from "./graph";

// One thing in the transcript: an operator question, a (streaming) assistant
// answer, or a rendered UI tool.
export type Item =
  | { kind: "user"; id: string; text: string }
  | { kind: "text"; id: string; text: string; open: boolean }
  | {
      kind: "tool";
      id: string; // the tool_use id
      block: ToolUseBlock;
      interactive: boolean;
      resolved: boolean;
    }
  | { kind: "servertool"; id: string; name: string; preview: string };

export interface AgentSession {
  items: Item[];
  busy: boolean;
  error: string | null;
  submit: (question: string) => Promise<void>;
  respond: (blockId: string, value: string) => void;
  stop: () => void;
  clear: () => void;
  // spec-10: save/re-load. snapshot() reads the current transcript + canonical
  // message history for persisting; load() rehydrates a saved chat (and seeds a
  // fresh graph so a follow-up turn re-seeds from the loaded history). The loaded
  // id/owner let the page tell a first-save from a re-save of an existing chat.
  snapshot: () => { messages: AgentMessage[]; items: Item[] };
  load: (
    messages: AgentMessage[],
    items: Item[],
    meta?: { id: string; savedBy: string; name: string },
  ) => void;
  loadedSessionId: string | null;
  loadedSavedBy: string | null;
  loadedName: string | null;
}

/** A pending interactive widget: resolve with the operator's choice, or reject
 * (abort) if the turn is stopped while waiting. */
interface Pending {
  resolve: (value: string) => void;
  reject: () => void;
}

const ABORT = () => new DOMException("Stopped by operator", "AbortError");
const isAbort = (e: unknown): boolean =>
  e instanceof DOMException ? e.name === "AbortError" : (e as { name?: string })?.name === "AbortError";

let counter = 0;
const uid = () => `i${Date.now().toString(36)}-${counter++}`;

// The chat engine: streaming transcript + the client ReAct loop (agent/graph.ts).
// State lives here (in a hook) rather than in the page component so a provider can
// hoist it ABOVE the router — navigating away and back keeps the session alive
// (in memory only; nothing is persisted, and the in-memory MemorySaver thread is
// reset by clear() / a full reload — spec-9 ac-34).
export function useAgentChat(transport: AgentTransport = httpTransport): AgentSession {
  const [items, setItems] = useState<Item[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The currently-loaded saved chat (null = an unsaved, in-memory chat). Drives
  // the page's first-save vs re-save decision (spec-10 t-3/t-5).
  const [loadedSessionId, setLoadedSessionId] = useState<string | null>(null);
  const [loadedSavedBy, setLoadedSavedBy] = useState<string | null>(null);
  const [loadedName, setLoadedName] = useState<string | null>(null);

  // The canonical message history sent to the server (kept across turns, never
  // persisted — ac-34).
  const historyRef = useRef<AgentMessage[]>([]);
  // Resolvers for interactive tools awaiting the operator's response.
  const pendingRef = useRef<Map<string, Pending>>(new Map());
  // Aborts the in-flight turn (the SSE completion / tool fetch) on stop / Esc.
  const abortRef = useRef<AbortController | null>(null);
  // The compiled graph is stable for the session.
  const graphRef = useRef<AgentGraph | null>(null);
  if (!graphRef.current) graphRef.current = createAgentGraph().graph;

  // Append a streamed text delta to the open assistant bubble, or open one.
  const onText = useCallback((delta: string) => {
    setItems((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.kind === "text" && last.open) {
        return [...prev.slice(0, -1), { ...last, text: last.text + delta }];
      }
      return [...prev, { kind: "text", id: uid(), text: delta, open: true }];
    });
  }, []);

  // Close any open assistant bubble (a tool render, or the turn ending, starts a
  // fresh bubble next time text streams).
  const closeOpenText = useCallback(() => {
    setItems((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.kind === "text" && last.open) {
        return [...prev.slice(0, -1), { ...last, open: false }];
      }
      return prev;
    });
  }, []);

  // Render a UI tool in the transcript. Display-only tools resolve at once;
  // interactive ones return a promise that resolves when respond() is called.
  const renderUiTool = useCallback(
    (block: ToolUseBlock): Promise<unknown> => {
      closeOpenText();
      const interactive = INTERACTIVE_UI_TOOLS.has(block.name);
      setItems((prev) => [
        ...prev,
        { kind: "tool", id: block.id, block, interactive, resolved: !interactive },
      ]);
      if (!interactive) return Promise.resolve({ displayed: true });
      return new Promise<unknown>((resolve, reject) => {
        pendingRef.current.set(block.id, {
          resolve: (value: string) => resolve({ choice: value }),
          reject: () => reject(ABORT()),
        });
      });
    },
    [closeOpenText],
  );

  // Surface a server tool call (run_sql, get_orgs, query_logs…) in the transcript
  // so the operator can see exactly what the agent ran (transparency).
  const onServerTool = useCallback(
    (toolBlock: ToolUseBlock, result: ToolResult) => {
      closeOpenText();
      const preview = result.content.slice(0, 280);
      setItems((prev) => [
        ...prev,
        { kind: "servertool", id: `${toolBlock.id}-srv`, name: toolBlock.name, preview },
      ]);
    },
    [closeOpenText],
  );

  // An operator answered an interactive widget: mark it resolved, disable it, and
  // resume the loop with their choice.
  const respond = useCallback((blockId: string, value: string) => {
    const pending = pendingRef.current.get(blockId);
    if (!pending) return;
    pendingRef.current.delete(blockId);
    setItems((prev) =>
      prev.map((it) =>
        it.kind === "tool" && it.id === blockId ? { ...it, resolved: true } : it,
      ),
    );
    pending.resolve(value);
  }, []);

  const submit = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || busy) return;
      setError(null);
      setBusy(true);
      setItems((prev) => [...prev, { kind: "user", id: uid(), text: q }]);

      const turnId = uid();
      const userMessage: AgentMessage = { role: "user", content: q };
      const messages = [...historyRef.current, userMessage];

      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const next = await runTurn({
          graph: graphRef.current!,
          turnId,
          prompt: q,
          messages,
          handlers: { onText, renderUiTool, onServerTool },
          transport,
          signal: controller.signal,
        });
        historyRef.current = next;
      } catch (e) {
        // A stop (Esc / Stop button) aborts the turn — that's not an error, so
        // leave the partial transcript and surface nothing.
        if (!isAbort(e)) {
          setError(e instanceof Error ? e.message : "Something went wrong.");
        }
      } finally {
        abortRef.current = null;
        closeOpenText();
        setBusy(false);
      }
    },
    [busy, onText, renderUiTool, onServerTool, closeOpenText, transport],
  );

  // Stop the current turn: abort the in-flight fetch AND reject any interactive
  // widget the loop is paused on, so the loop unwinds either way.
  const stop = useCallback(() => {
    abortRef.current?.abort();
    for (const pending of pendingRef.current.values()) pending.reject();
    pendingRef.current.clear();
  }, []);

  // Wipe the conversation: empty transcript, drop history + any pending widgets,
  // and start a fresh graph thread so the model has no memory of the old chat.
  const clear = useCallback(() => {
    abortRef.current?.abort();
    pendingRef.current.clear();
    historyRef.current = [];
    graphRef.current = createAgentGraph().graph;
    setItems([]);
    setError(null);
    setLoadedSessionId(null);
    setLoadedSavedBy(null);
    setLoadedName(null);
  }, []);

  // Read the current state for saving: the canonical message history (sent to the
  // server next turn) and the rendered transcript items.
  const snapshot = useCallback(
    () => ({ messages: historyRef.current, items }),
    [items],
  );

  // Rehydrate a saved chat: render its items, restore its history, and start a
  // fresh graph thread (like clear()) so a follow-up submit() re-seeds the model
  // from the loaded history (historyRef + the new user message).
  const load = useCallback(
    (
      messages: AgentMessage[],
      loadedItems: Item[],
      meta?: { id: string; savedBy: string; name: string },
    ) => {
      abortRef.current?.abort();
      pendingRef.current.clear();
      historyRef.current = messages;
      graphRef.current = createAgentGraph().graph;
      setItems(loadedItems);
      setError(null);
      setLoadedSessionId(meta?.id ?? null);
      setLoadedSavedBy(meta?.savedBy ?? null);
      setLoadedName(meta?.name ?? null);
    },
    [],
  );

  return {
    items,
    busy,
    error,
    submit,
    respond,
    stop,
    clear,
    snapshot,
    load,
    loadedSessionId,
    loadedSavedBy,
    loadedName,
  };
}

const AgentSessionContext = createContext<AgentSession | null>(null);

/**
 * Hoists one agent chat session above the router so it outlives route changes —
 * mount it around the authenticated routes. Keeps the session in memory only.
 */
export function AgentSessionProvider({
  children,
  transport = httpTransport,
}: {
  children: ReactNode;
  transport?: AgentTransport;
}) {
  const session = useAgentChat(transport);
  return (
    <AgentSessionContext.Provider value={session}>{children}</AgentSessionContext.Provider>
  );
}

/**
 * The shared session when a provider is present; otherwise a component-local one
 * (used by tests that render the page standalone with an injected transport).
 * Both hooks always run to satisfy the rules of hooks — the unused one is inert.
 */
export function useAgentSession(fallbackTransport?: AgentTransport): AgentSession {
  const shared = useContext(AgentSessionContext);
  const local = useAgentChat(fallbackTransport);
  return shared ?? local;
}
